import { Slider, SliderThumb } from '@mui/material';
import { styled } from '@mui/system';
import { exists } from 'neuro-utils';
import * as React from 'react';
import colors from '../../../config/theme/colors';
import theme from '../../../config/theme/muiTheme';

const ThumbIcon = ({
  compact,
  dragging,
  grayOut,
  children,
}: {
  compact?: boolean;
  dragging: boolean;
  grayOut: boolean;
  children: React.JSX.Element;
}): React.JSX.Element => {
  return (
    <div>
      <svg
        width={compact ? '33' : '33'}
        height={compact ? '35' : '41'}
        viewBox="0 0 39 47"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <filter id="shadow">
            <feDropShadow dx="0" dy="0" stdDeviation={2} floodColor={colors.appBlue.medium} />
          </filter>
        </defs>
        <path
          d="M16.5 0C25.6066 0 33 7.32071 33 16.3378C33 25.355 16.4909 41.3799 16.5 40.9931C16.5016 40.9255 0 25.355 0 16.3378C0 7.32071 7.39337 0 16.5 0Z"
          fill={grayOut ? colors.gray : colors.appBlue.default}
          filter={dragging && !grayOut ? 'url(#shadow)' : undefined}
          style={{ transform: 'translate(3px, 3px)' }}
        />
      </svg>
      <div
        style={{
          position: 'absolute',
          left: compact ? '0' : '50%',
          transform: 'translateX(-50%)',
          top: '-1.8rem',
        }}
      >
        {children}
      </div>
    </div>
  );
};

const StyledSliderThumb = styled(SliderThumb, {
  shouldForwardProp: prop => prop !== 'compact',
})(({ compact }: { compact?: boolean }) => ({
  width: 1,
  height: 1,
  transform: compact ? 'translateY(-1.7rem)' : 'translateY(-2.6rem)',
  '&:focus, &:hover, &.Mui-active, &.Mui-focusVisible': {
    boxShadow: '0px 0px',
  },
}));

const StyledRangeSlider = styled(Slider)({
  '& .MuiSlider-track': {
    backgroundColor: colors.appBlue.lightest,
    borderRadius: '0px',
    border: '0px',
    transform: 'translate(1px, -2px)',
  },
  '& .MuiSlider-rail': {
    backgroundColor: colors.white,
    height: '0.6rem',
    borderRadius: '0px',
    opacity: 1,
    border: '1px solid black',
    borderTop: '0px',
  },
  '& .MuiSlider-mark': {
    backgroundColor: 'black',
    height: 8,
    width: 1,
  },
});

const RangeText = styled('div')({
  height: '2rem',
  minHeight: '2rem',
  padding: '0rem 1.3rem',
  color: colors.primaryText,
  fontWeight: 600,
});

const CustomThumb = (props: { compact?: boolean } & React.HTMLAttributes<unknown>) => {
  const { children, ...other } = props;

  const childElement = React.isValidElement<{ value?: number; disabled?: boolean }>(children) ? children : null;
  const value: number | undefined = childElement?.props.value;
  const grayOut: boolean = childElement?.props.disabled ?? false;
  const [dragging, setDragging] = React.useState<boolean>(false);
  return (
    <StyledSliderThumb {...other} onFocus={() => setDragging(true)} onBlur={() => setDragging(false)}>
      {children}
      <ThumbIcon compact={other.compact} dragging={dragging} grayOut={grayOut}>
        <span
          style={{
            color: colors.white,
            fontSize: other.compact ? '1.2rem' : theme.typography.fontSize,
            userSelect: 'none',
          }}
        >
          {value}
        </span>
      </ThumbIcon>
    </StyledSliderThumb>
  );
};

const RangeSlider = ({
  editing = false,
  name,
  onChange,
  value,
  min,
  max,
  marks,
  showRangeText = false,
  disabled,
  compact,
  height,
}: IInputBasics & IRangeSlider): React.JSX.Element => {
  const grayOut = !editing || disabled;

  const displayValue = value ?? [min, max];

  const handleValueChange = (event: Event, newValue: number | number[]) => {
    if (!grayOut && onChange && Array.isArray(newValue)) {
      onChange({ [name]: newValue });
    }
  };
  return (
    <>
      <div
        style={{
          width: '100%',
          height: height ? height + 'rem' : '8rem',
          display: 'flex',
          alignItems: 'end',
          padding: '0rem 1.3rem',
        }}
      >
        <StyledRangeSlider
          value={displayValue}
          onChange={handleValueChange}
          slots={{ thumb: CustomThumb }}
          slotProps={{ thumb: () => ({ compact: compact }) }}
          marks={marks}
          min={min}
          max={max}
          disabled={grayOut}
        />
      </div>
      {showRangeText && (
        <RangeText>{exists(value?.[0]) && exists(value?.[1]) ? `${value?.[0]} - ${value?.[1]}` : ''}</RangeText>
      )}
    </>
  );
};

export default RangeSlider;
